import { useState } from 'react';

export default function Calculators() {
  const [activeCalc, setActiveCalc] = useState<'sip' | 'insurance'>('sip');

  // SIP Calculator State
  const [sipMonthly, setSipMonthly] = useState(5000);
  const [sipYears, setSipYears] = useState(10);
  const [sipReturn, setSipReturn] = useState(12);

  // Insurance Calculator State
  const [age, setAge] = useState(30);
  const [income, setIncome] = useState(500000);
  const [dependents, setDependents] = useState(2);

  // SIP Calculation
  const calculateSIP = () => {
    const monthlyRate = sipReturn / 12 / 100;
    const months = sipYears * 12;
    const futureValue =
      sipMonthly * (((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) * (1 + monthlyRate));
    const invested = sipMonthly * months;
    const returns = futureValue - invested;
    return { invested, returns, total: futureValue };
  };

  // Insurance Calculation (Simple formula: 10-15x annual income + liabilities, minimum ₹1 Cr)
  const calculateInsurance = () => {
    const annualIncome = income * 12;
    const calculatedMin = annualIncome * 10;
    const calculatedRecommended = annualIncome * 15;
    
    // Ensure minimum of 1 Crore considering inflation
    const minCoverage = Math.max(calculatedMin, 10000000);
    const recommendedCoverage = Math.max(calculatedRecommended, 10000000);
    
    const monthlyPremium = Math.round((recommendedCoverage * 0.0005) / 12); // Rough estimate
    return { minCoverage, recommendedCoverage, monthlyPremium };
  };

  const sipResults = calculateSIP();
  const insuranceResults = calculateInsurance();

  const formatCurrency = (num: number) => {
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    return `₹${num.toLocaleString('en-IN')}`;
  };

  return (
    <section id="calculators" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Financial <span className="text-blue-600">Calculators</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Plan your financial future with our interactive calculators
          </p>
        </div>

        {/* Calculator Toggle */}
        <div className="flex justify-center mb-4">
          <div className="inline-flex bg-gray-100 rounded-xl p-1">
            <button
              onClick={() => setActiveCalc('sip')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeCalc === 'sip'
                  ? 'bg-white text-blue-600 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              SIP Calculator
            </button>
            <button
              onClick={() => setActiveCalc('insurance')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                activeCalc === 'insurance'
                  ? 'bg-white text-blue-600 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Insurance Calculator
            </button>
          </div>
        </div>

        {/* Inflation Calculator Link */}
        <div className="flex justify-center mb-8">
          <a
            href="https://www.sbisecurities.in/calculators/inflation-calculator"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 text-sm font-medium underline underline-offset-2 transition-colors"
          >
            Inflation Calculator →
          </a>
        </div>

        <div className="max-w-5xl mx-auto">
          {/* SIP Calculator */}
          {activeCalc === 'sip' && (
            <div className="grid md:grid-cols-2 gap-8">
              {/* Inputs */}
              <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-3xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Calculate Your SIP Returns
                </h3>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Monthly Investment
                    </label>
                    <input
                      type="range"
                      min="500"
                      max="50000"
                      step="500"
                      value={sipMonthly}
                      onChange={(e) => setSipMonthly(Number(e.target.value))}
                      className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="text-2xl font-bold text-blue-600 mt-2">
                      ₹{sipMonthly.toLocaleString()}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Investment Period (Years)
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="30"
                      value={sipYears}
                      onChange={(e) => setSipYears(Number(e.target.value))}
                      className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="text-2xl font-bold text-blue-600 mt-2">{sipYears} Years</div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Expected Return (% per annum)
                    </label>
                    <input
                      type="range"
                      min="6"
                      max="30"
                      step="0.5"
                      value={sipReturn}
                      onChange={(e) => setSipReturn(Number(e.target.value))}
                      className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="text-2xl font-bold text-blue-600 mt-2">{sipReturn}%</div>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Returns</h3>

                <div className="space-y-6">
                  <div className="bg-blue-50 rounded-xl p-6">
                    <div className="text-sm text-gray-600 mb-1">Total Invested</div>
                    <div className="text-3xl font-bold text-blue-600">
                      {formatCurrency(sipResults.invested)}
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-xl p-6">
                    <div className="text-sm text-gray-600 mb-1">Expected Returns</div>
                    <div className="text-3xl font-bold text-green-600">
                      {formatCurrency(sipResults.returns)}
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-600 to-green-600 rounded-xl p-6 text-white">
                    <div className="text-sm mb-1">Total Value</div>
                    <div className="text-4xl font-bold">{formatCurrency(sipResults.total)}</div>
                  </div>

                  <a
                    href="#contact"
                    className="block text-center bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-all font-semibold"
                  >
                    Start Your SIP Today
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Insurance Calculator */}
          {activeCalc === 'insurance' && (
            <div className="grid md:grid-cols-2 gap-8">
              {/* Inputs */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Calculate Coverage Needed
                </h3>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Your Age
                    </label>
                    <input
                      type="range"
                      min="20"
                      max="60"
                      value={age}
                      onChange={(e) => setAge(Number(e.target.value))}
                      className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="text-2xl font-bold text-blue-600 mt-2">{age} Years</div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Monthly Income
                    </label>
                    <input
                      type="range"
                      min="20000"
                      max="500000"
                      step="10000"
                      value={income}
                      onChange={(e) => setIncome(Number(e.target.value))}
                      className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="text-2xl font-bold text-blue-600 mt-2">
                      ₹{income.toLocaleString()}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Number of Dependents
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="6"
                      value={dependents}
                      onChange={(e) => setDependents(Number(e.target.value))}
                      className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="text-2xl font-bold text-blue-600 mt-2">{dependents}</div>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Recommended Coverage
                </h3>

                <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-4 mb-6">
                  <p className="text-xs font-semibold text-yellow-800 mb-1">💡 Inflation Protection</p>
                  <p className="text-xs text-gray-700">
                    We recommend a minimum of ₹1 Crore coverage to account for inflation and rising living costs.
                  </p>
                </div>

                <div className="space-y-6">
                  <div className="bg-blue-50 rounded-xl p-6">
                    <div className="text-sm text-gray-600 mb-1">Minimum Coverage</div>
                    <div className="text-3xl font-bold text-blue-600">
                      {formatCurrency(insuranceResults.minCoverage)}
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
                    <div className="text-sm mb-1">Recommended Coverage</div>
                    <div className="text-4xl font-bold">
                      {formatCurrency(insuranceResults.recommendedCoverage)}
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-xl p-6">
                    <div className="text-sm text-gray-600 mb-1">Estimated Monthly Premium</div>
                    <div className="text-3xl font-bold text-green-600">
                      ₹{insuranceResults.monthlyPremium.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">*Approximate value</div>
                  </div>

                  <a
                    href="#contact"
                    className="block text-center bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-all font-semibold"
                  >
                    Get Exact Quote
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
